import React from 'react';
import { Card } from './UIComponents';
import { Clock, FileAudio, Calendar, AlertTriangle, TrendingUp } from 'lucide-react';
import type { Quota } from '../types';

interface UsageStatsProps {
  quota: Quota;
  compact?: boolean;
}

function formatMinutes(minutes: number): string {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (mins === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${mins}m`;
  }
  return `${Math.round(minutes)}m`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatUTCTimestamp(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '00:00 UTC';
  }
  return `${date.toLocaleString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })} UTC`;
}

function getUsagePercentage(used: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, (used / total) * 100);
}

function getUsageColor(percentage: number): string {
  if (percentage >= 100) return 'bg-red-500';
  if (percentage >= 80) return 'bg-amber-500';
  return 'bg-indigo-500';
}

const UsageStats: React.FC<UsageStatsProps> = ({ quota, compact = false }) => {
  const minutesPercentage = getUsagePercentage(quota.used_minutes, quota.monthly_minutes);
  const filesPercentage = quota.daily_files_limit > 0
    ? getUsagePercentage(quota.daily_files_used, quota.daily_files_limit)
    : 0;

  const isLowOnMinutes = minutesPercentage >= 80;
  const isOutOfMinutes = quota.remaining_minutes <= 0;

  if (compact) {
    return (
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
          <span className="text-sm text-slate-600 dark:text-zinc-400">
            {formatMinutes(quota.remaining_minutes)} left
          </span>
        </div>
        <div className="flex items-center gap-2">
          <FileAudio className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
          <span className="text-sm text-slate-600 dark:text-zinc-400">
            {quota.daily_files_limit === -1
              ? 'Unlimited'
              : `${quota.daily_files_limit - quota.daily_files_used} files today`}
          </span>
        </div>
      </div>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Usage This Period</h3>
        {quota.period_start && quota.period_end && (
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-zinc-400">
            <Calendar className="h-4 w-4" />
            {formatDate(quota.period_start)} - {formatDate(quota.period_end)}
          </div>
        )}
      </div>

      {/* Minutes Usage */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
            <span className="text-sm font-medium text-slate-700 dark:text-zinc-300">Transcription Minutes</span>
          </div>
          <span className="text-sm text-slate-600 dark:text-zinc-400">
            {formatMinutes(quota.used_minutes)} / {formatMinutes(quota.monthly_minutes)}
          </span>
        </div>
        <div className="h-3 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden">
          <div
            className={`h-full ${getUsageColor(minutesPercentage)} transition-all duration-300`}
            style={{ width: `${minutesPercentage}%` }}
          />
        </div>
        <p className="mt-1 text-xs text-slate-500 dark:text-zinc-500">
          {formatMinutes(quota.remaining_minutes)} remaining
        </p>
      </div>

      {/* Daily Files */}
      {quota.daily_files_limit !== -1 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileAudio className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
              <span className="text-sm font-medium text-slate-700 dark:text-zinc-300">Daily Uploads</span>
            </div>
            <span className="text-sm text-slate-600 dark:text-zinc-400">
              {quota.daily_files_used} / {quota.daily_files_limit}
            </span>
          </div>
          <div className="h-3 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden">
            <div
              className={`h-full ${getUsageColor(filesPercentage)} transition-all duration-300`}
              style={{ width: `${filesPercentage}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-zinc-500">
            {quota.can_upload_today
              ? `${quota.daily_files_limit - quota.daily_files_used} uploads remaining today`
              : quota.daily_reset_at
                ? `Daily limit reached, resets at ${formatUTCTimestamp(quota.daily_reset_at)}`
                : 'Daily limit reached, resets at 00:00 UTC'}
          </p>
        </div>
      )}

      {quota.daily_files_limit === -1 && (
        <div className="mb-6 flex items-center gap-2 text-sm text-slate-600 dark:text-zinc-400">
          <FileAudio className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
          <span>Unlimited daily uploads</span>
        </div>
      )}

      {/* Warning Messages */}
      {isOutOfMinutes && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-300">You've used all your minutes</p>
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              Upgrade your plan to continue transcribing
            </p>
          </div>
        </div>
      )}

      {isLowOnMinutes && !isOutOfMinutes && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
          <TrendingUp className="h-5 w-5 text-amber-500 dark:text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Running low on minutes</p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              Consider upgrading to avoid interruption
            </p>
          </div>
        </div>
      )}
    </Card>
  );
};

export default UsageStats;
